// CROSSSELL-109 / FUNCTIONALITY-112 — deterministic Store V2 cross-sell rules (no AI).

import type { CatalogV2Product } from "./catalog-v2-types.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { resolveProductTitle } from "./product-title-resolver.js";

/** Source category → preferred recommendation category ids (priority order). */
export const CROSS_SELL_CATEGORY_RULES: Record<string, string[]> = {
  biz_ramen_buldak: ["biz_sauces", "biz_snacks", "biz_seaweed", "biz_drinks"],
  biz_coconut_milk: ["biz_pastes_spices", "biz_rice_noodles", "biz_sauces"],
  biz_sauces: ["biz_rice_noodles", "biz_snacks", "biz_seaweed"],
  biz_asian_tea: ["biz_desserts", "biz_snacks", "biz_fruit_concentrates"],
  biz_snacks: ["biz_drinks", "biz_sauces"],
  biz_caviar_seafood: ["biz_sauces", "biz_rice_noodles", "biz_seaweed"],
  biz_rice_noodles: ["biz_sauces", "biz_pastes_spices", "biz_snacks"],
  biz_cooking_sets: ["biz_sauces", "biz_rice_noodles", "biz_pastes_spices", "biz_coconut_milk", "biz_seaweed"],
  biz_pastes_spices: ["biz_rice_noodles", "biz_coconut_milk", "biz_sauces"],
  biz_seaweed: ["biz_rice_noodles", "biz_sauces", "biz_snacks", "biz_caviar_seafood"],
  biz_drinks: ["biz_snacks", "biz_desserts"],
  biz_desserts: ["biz_asian_tea", "biz_drinks"],
  // 112 — weak categories coverage
  biz_fruit_concentrates: ["biz_asian_tea", "biz_drinks", "biz_desserts"],
  biz_spring_rolls: ["biz_sauces", "biz_rice_noodles", "biz_veg_fruit_preserve"],
  biz_veg_fruit_preserve: ["biz_sauces", "biz_rice_noodles", "biz_snacks"],
  biz_diet: ["biz_asian_tea", "biz_snacks", "biz_drinks"],
  biz_equipment: ["biz_rice_noodles", "biz_sauces", "biz_coconut_milk", "biz_pastes_spices"],
};

/** Global safe fallback category order when curated rules are thin. */
export const CROSS_SELL_GLOBAL_FALLBACK = [
  "biz_sauces",
  "biz_rice_noodles",
  "biz_snacks",
  "biz_drinks",
  "biz_desserts",
  "biz_ramen_buldak",
  "biz_coconut_milk",
] as const;

export interface CrossSellCandidate {
  product: CatalogV2Product;
  title: string;
  reasonCategory: string;
}

export function crossSellSubtitleForCategories(sourceKeys: string[], lang: StoreV2Lang = "ua"): string {
  const key = sourceKeys[0] ?? "";
  if (lang === "en") {
    if (key === "biz_seaweed") return "Nori pairs well with rice, soy sauce, or sesame.";
    if (key === "biz_coconut_milk") return "Coconut milk often goes with curry paste or rice.";
    if (key === "biz_rice_noodles" || key === "biz_ramen_buldak") return "Noodles pair well with sauce or snacks.";
    if (key === "biz_equipment") return "Add basic products for your first cook.";
    return "Customers often add these together.";
  }
  if (key === "biz_seaweed") return "До норі зручно додати рис, соєвий соус або кунжут.";
  if (key === "biz_coconut_milk") return "До кокосового молока часто беруть пасту карі або рис.";
  if (key === "biz_rice_noodles" || key === "biz_ramen_buldak") return "До локшини часто додають соус або снеки.";
  if (key === "biz_equipment") return "До обладнання додавайте базові продукти для першого приготування.";
  return "Часто беруть разом із цим товаром.";
}

function cartCategoryKeys(cartIds: Set<string>, catalog: CatalogV2Product[]): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const p of catalog) {
    if (!cartIds.has(p.id)) continue;
    for (const k of p.categoryKeys) {
      if (!seen.has(k)) {
        seen.add(k);
        keys.push(k);
      }
    }
  }
  return keys;
}

function targetCategories(sourceKeys: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const src of sourceKeys) {
    for (const t of CROSS_SELL_CATEGORY_RULES[src] ?? []) {
      if (!seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }
  }
  for (const t of CROSS_SELL_GLOBAL_FALLBACK) {
    if (!seen.has(t) && !sourceKeys.includes(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  // Equipment: never recommend more random equipment as fill.
  if (sourceKeys.includes("biz_equipment")) {
    return out.filter((t) => t !== "biz_equipment");
  }
  return out;
}

function isoWeekNumber(date = new Date()): number {
  const tmp = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil((((tmp.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function stableSeed(parts: Array<string | number | null | undefined>): number {
  const raw = parts.map((part) => String(part ?? "")).join("|");
  let hash = 2166136261;
  for (const ch of raw) hash = Math.imul(hash ^ ch.charCodeAt(0), 16777619);
  return hash >>> 0;
}

function seededRank(seed: number, id: string): number {
  let hash = seed ^ 0x9e3779b9;
  for (const ch of id) hash = Math.imul(hash ^ ch.charCodeAt(0), 16777619);
  return hash >>> 0;
}

function isEligible(p: CatalogV2Product, cartIds: Set<string>): boolean {
  if (cartIds.has(p.id)) return false;
  if (!p.isSellable) return false;
  if (!p.isPriced || p.priceUah == null || p.priceUah <= 0) return false;
  if (p.stockStatus === "out_of_stock" || p.stockStatus === "unknown") return false;
  return true;
}

function toCandidate(p: CatalogV2Product, reasonCategory: string, lang: StoreV2Lang): CrossSellCandidate {
  const resolved = resolveProductTitle(
    {
      id: p.id,
      sku: p.sku,
      title: p.title,
      titleRu: p.titleRu,
      brand: p.brand,
      categoryName: p.categoryName,
      categoryKeys: p.categoryKeys,
    },
    lang,
  );
  return { product: p, title: resolved.title, reasonCategory };
}

/**
 * Ranked cross-sell pool (deterministic). Used for paging refresh without random reshuffle.
 */
export function rankRecommendationsForCart(
  catalog: CatalogV2Product[],
  cartProductIds: string[],
  lang: StoreV2Lang = "ua",
  poolMax = 12,
  options: { customerSegment?: string; seedExtra?: string | null } = {},
): CrossSellCandidate[] {
  const cartIds = new Set(cartProductIds.filter(Boolean));
  if (cartIds.size === 0) return [];

  for (const cartId of cartIds) {
    const source = catalog.find((p) => p.id === cartId);
    const peerSku = source?.canaryCrossSellPeerSku?.trim();
    if (!peerSku) continue;
    const peer = catalog.find((p) => p.merchantSku === peerSku || p.sku === peerSku);
    if (peer && isEligible(peer, cartIds)) {
      return [toCandidate(peer, source?.categoryKeys[0] ?? "biz_ramen_buldak", lang)];
    }
  }

  const sourceKeys = cartCategoryKeys(cartIds, catalog);
  const targets = targetCategories(sourceKeys);
  const seed = stableSeed([
    cartProductIds[0] ?? "cart",
    options.customerSegment ?? "anon",
    lang,
    isoWeekNumber(),
    options.seedExtra ?? null,
  ]);

  const picked: CrossSellCandidate[] = [];
  const pickedIds = new Set<string>();
  const usedBrands = new Set<string>();
  const usedCategories = new Set<string>();
  const limit = Math.min(Math.max(poolMax, 3), 18);

  const consider = (candidate: CatalogV2Product, reasonCategory: string) => {
    if (picked.length >= limit) return false;
    if (pickedIds.has(candidate.id) || !isEligible(candidate, cartIds)) return false;
    const brandKey = (candidate.brand ?? "").trim().toLocaleLowerCase("en");
    if (brandKey && usedBrands.has(brandKey)) return false;
    picked.push(toCandidate(candidate, reasonCategory, lang));
    pickedIds.add(candidate.id);
    if (brandKey) usedBrands.add(brandKey);
    for (const key of candidate.categoryKeys) usedCategories.add(key);
    return true;
  };

  const poolFor = (predicate: (p: CatalogV2Product) => boolean) =>
    catalog
      .filter((p) => predicate(p) && isEligible(p, cartIds) && !pickedIds.has(p.id))
      .sort((a, b) => seededRank(seed, a.id) - seededRank(seed, b.id) || a.id.localeCompare(b.id));

  for (const target of targets) {
    for (const hit of poolFor((p) => p.categoryKeys.includes(target))) {
      if (consider(hit, target)) break;
    }
  }

  for (const hit of poolFor((p) => !p.categoryKeys.some((key) => usedCategories.has(key)))) {
    consider(hit, pCategory(hit));
    if (picked.length >= limit) break;
  }

  for (const hit of poolFor(() => true)) {
    consider(hit, pCategory(hit));
    if (picked.length >= limit) break;
  }

  return picked;
}

/**
 * Pick up to `max` recommendations for the current cart.
 * Prefer different categories; deterministic order by rule + catalog order.
 * `page` advances through the ranked pool (refresh → next set).
 */
export function recommendForCart(
  catalog: CatalogV2Product[],
  cartProductIds: string[],
  lang: StoreV2Lang = "ua",
  max = 4,
  options: { customerSegment?: string; seedExtra?: string | null; page?: number } = {},
): CrossSellCandidate[] {
  const pageSize = Math.min(Math.max(max, 2), 3);
  const pool = rankRecommendationsForCart(catalog, cartProductIds, lang, pageSize * 4, options);
  if (pool.length === 0) return [];
  const page = Math.max(0, Math.trunc(options.page ?? 0));
  const start = (page * pageSize) % pool.length;
  const out: CrossSellCandidate[] = [];
  for (let i = 0; i < pageSize && i < pool.length; i += 1) {
    out.push(pool[(start + i) % pool.length]!);
  }
  return out;
}

function pCategory(product: CatalogV2Product): string {
  return product.categoryKeys[0] ?? "global";
}

/** PDP “pairs well” — same engine with a single product as the cart seed. */
export function recommendForProduct(
  catalog: CatalogV2Product[],
  product: CatalogV2Product,
  lang: StoreV2Lang = "ua",
  max = 4,
  options: { customerSegment?: string } = {},
): CrossSellCandidate[] {
  return recommendForCart(catalog, [product.id], lang, max, {
    customerSegment: options.customerSegment ?? "anon",
    seedExtra: product.id,
  });
}

/** Guard helper: unrelated products should not share an identical fallback set. */
export function crossSellDiversityProbe(
  catalog: CatalogV2Product[],
  productIds: string[],
  lang: StoreV2Lang = "ua",
): boolean {
  if (productIds.length < 2) return true;
  const sets = productIds.map((id) => {
    const product = catalog.find((row) => row.id === id);
    if (!product) return "";
    return recommendForProduct(catalog, product, lang, 4)
      .map((item) => item.product.id)
      .join("|");
  });
  return new Set(sets).size > 1;
}
